import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import GavelIcon from "@mui/icons-material/Gavel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  GetTenantViolations,
  SubmitApology,
  violationBody,
} from "../../../redux/reducer/AgreementViolation";

const STATUS_COLOR = {
  OPEN: "warning",
  ACKNOWLEDGED: "info",
  RESOLVED: "success",
  ESCALATED: "error",
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString("en-US")}`;

// The tenant's view of what has been logged against them.
//
// Deliberately has no "reject" control. A violation is the landlord's
// first-hand record of something that happened; the tenant's move is to own it
// and commit to not repeating it. Whether that's enough is the landlord's call
// — which is why the only action here is "Send response".
export default function TenantViolationPanel({ tenantId }) {
  const dispatch = useDispatch();
  const { violations, loading } = useSelector(
    (state) => state.agreementViolation
  );

  const [draft, setDraft] = React.useState({});
  const [sendingId, setSendingId] = React.useState(null);

  React.useEffect(() => {
    if (tenantId) dispatch(GetTenantViolations({ tenantId }));
  }, [dispatch, tenantId]);

  const handleSend = async (violationId) => {
    const message = (draft[violationId] || "").trim();
    if (!message) {
      toast.warning("Please write your response first.");
      return;
    }

    setSendingId(violationId);
    try {
      const res = await dispatch(SubmitApology({ id: violationId, message }));
      const body = violationBody(res);

      if (body?.success) {
        toast.success(body.message || "Your response has been sent to the owner.");
        setDraft((prev) => ({ ...prev, [violationId]: "" }));
      } else {
        toast.error(body?.message || "Could not send response");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setSendingId(null);
    }
  };

  if (loading && violations.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!violations.length) return null;

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid #e5e7eb" }}>
      <Typography
        variant="subtitle1"
        fontWeight={800}
        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
      >
        <GavelIcon fontSize="small" color="warning" /> Agreement Violations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These were logged by your owner. You can't dismiss a violation, but you
        can respond — if the owner accepts your response, it's marked resolved
        and any related move-out request is withdrawn.
      </Typography>

      <Stack spacing={2}>
        {violations.map((v) => {
          const pending = v.apology?.decision === "PENDING";
          const accepted = v.apology?.decision === "ACCEPTED";
          const rejected = v.apology?.decision === "REJECTED";
          const canRespond = v.status !== "RESOLVED" && !pending;

          return (
            <Box
              key={v._id}
              sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb" }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={v.reason} />
                  <Chip
                    size="small"
                    label={v.status}
                    color={STATUS_COLOR[v.status] || "default"}
                  />
                  {v.severity === "HIGH" && (
                    <Chip
                      size="small"
                      icon={<WarningAmberIcon />}
                      label="High"
                      color="error"
                      variant="outlined"
                    />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(v.reportedAt).format("DD MMM YYYY")}
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mt: 1 }}>
                {v.description}
              </Typography>

              {v.evidence?.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                  {v.evidence.map((file, i) => (
                    <Chip
                      key={file.publicId || i}
                      size="small"
                      variant="outlined"
                      icon={<AttachFileIcon fontSize="small" />}
                      label={`Evidence ${i + 1}`}
                      component="a"
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                    />
                  ))}
                </Stack>
              )}

              {v.systemEvidence && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    System record (rent ledger)
                  </Typography>
                  <Typography variant="body2">
                    Due {v.systemEvidence.dueDate} · {v.systemEvidence.daysOverdue} days
                    overdue · late fee {money(v.systemEvidence.penalty)} · total
                    outstanding {money(v.systemEvidence.totalOutstanding)}
                  </Typography>
                </Box>
              )}

              {v.adminReview?.status === "PENDING" && (
                <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                  The owner has requested an urgent move-out over this report —
                  it's under admin review before anything is finalized.
                </Alert>
              )}

              {v.status === "ESCALATED" && !accepted && (
                <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>
                  This has been escalated to a move-out request. Responding here
                  is your route to having it withdrawn — the request itself
                  can't be rejected.
                </Alert>
              )}

              {pending && (
                <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                  Your response is with the owner:
                  <em> "{v.apology.message}"</em>
                </Alert>
              )}

              {accepted && (
                <Alert severity="success" variant="outlined" sx={{ mt: 1.5 }}>
                  Owner accepted your response — resolved.
                  {v.apology.decisionNote ? ` "${v.apology.decisionNote}"` : ""}
                </Alert>
              )}

              {rejected && (
                <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                  Owner did not accept your response.
                  {v.apology.decisionNote ? ` "${v.apology.decisionNote}"` : ""}
                  {" You can send another response."}
                </Alert>
              )}

              {canRespond && (
                <Box sx={{ mt: 1.5 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Explain what happened and what you'll do differently…"
                    value={draft[v._id] || ""}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, [v._id]: e.target.value }))
                    }
                  />
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
                    disabled={sendingId === v._id}
                    onClick={() => handleSend(v._id)}
                  >
                    {sendingId === v._id ? "Sending…" : "Send response to owner"}
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
